import { localizedStrings } from '@core/strings'
import React from 'react'
import { Box, Flex, Text } from '@common/fundamentals'
import { Button, IconButton } from '@common/material'
import {
  ISiteBoxDocumentConfigResult,
  SiteBoxDocumentConfigResult,
} from '@core/entities/SiteBoxDocumentConfig'
import { ArrowBack } from '@mui/icons-material'
import { Card } from '@mui/material'
import { QueryAppraisalStatus } from '@core/entities/SiteBoxDocumentConfig/types'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'

type DocumentOverallAppraisalViewProps = {
  results: ISiteBoxDocumentConfigResult[]
  onBackClick: () => void
  setPDFSearchState?: (search: string) => void
  clearPDFSearchState: () => void
  onStartReviewClick: (startIndex: number) => void
  canUserAppraiseDocument: boolean
  documentEngineTemplateName: string
  readOnly?: boolean
}

export const DocumentOverallAppraisalView: React.FC<
  DocumentOverallAppraisalViewProps
> = ({
  results,
  onBackClick,
  setPDFSearchState,
  clearPDFSearchState,
  onStartReviewClick,
  canUserAppraiseDocument,
  documentEngineTemplateName,
  readOnly,
}) => {
  return (
    <Flex flexDirection='column' height='100%' px={2}>
      <Flex alignItems='center' gap={1} height={44}>
        <IconButton
          onClick={() => {
            clearPDFSearchState?.()
            onBackClick()
          }}
        >
          <ArrowBack />
        </IconButton>
        <Text fontSize={12}>{documentEngineTemplateName}</Text>
      </Flex>

      <Box flex={1} overflow='auto'>
        {results.map((result, index) => {
          const resultEntity = SiteBoxDocumentConfigResult.new(result)
          const evidence = result.result?.evidence || []
          const pages = resultEntity.getEvidencePages()

          const firstUnappraisedIndex =
            SiteBoxDocumentConfigResult.findFirstUnappraisedIndex(results)
          const isFirstUnappraised = index === firstUnappraisedIndex

          const handleEvidenceClick = (quote: string) => {
            setPDFSearchState?.(quote)
          }
          const getAppraisalStatus = (userResponse: QueryAppraisalStatus) => {
            if (userResponse === 'PASS') {
              return <CheckCircleIcon color='success' />
            }

            if (result.userResponse === 'FAIL') {
              return <CancelIcon color='error' />
            }

            if (result.userResponse === 'NA') {
              return <PriorityHighIcon color='warning' />
            }

            return <RadioButtonUncheckedIcon color='disabled' />
          }
          return (
            <Card
              key={result.id}
              variant='outlined'
              onClick={() => onStartReviewClick(index)}
              sx={{
                mb: 1,
                p: 1.5,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Flex alignItems='flex-start' gap={1}>
                <Flex justifyContent='center' alignSelf='center'>
                  {getAppraisalStatus(result.userResponse)}
                </Flex>
                <Box flex={1}>
                  <Text fontSize={12}>{result.query}</Text>
                  {pages.length > 0 && (
                    <Text
                      fontSize={12}
                      color='primary'
                      component='span'
                      sx={{
                        display: 'inline-block',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleEvidenceClick(evidence[0].quote)
                      }}
                    >
                      ({localizedStrings.page} {pages.join(', ')})
                    </Text>
                  )}
                </Box>
              </Flex>
              {!readOnly && canUserAppraiseDocument && isFirstUnappraised && (
                <Flex mt={2} width='100%'>
                  <Button
                    variant='contained'
                    size='small'
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation()
                      onStartReviewClick(index)
                    }}
                  >
                    {localizedStrings.review}
                  </Button>
                </Flex>
              )}
            </Card>
          )
        })}
      </Box>
    </Flex>
  )
}
